import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'utils/i18n';
import { useDarkMode } from 'utils/darkMode';
import sharedTranslations from '../shared/translations';
import { sections, typography, colors, spacing, interactive } from './docsStyles';
import MermaidDiagram from '../MermaidDiagram';

// Minimal markdown rendering (headings, lists, paragraphs, mermaid diagrams)
function renderMarkdown(md, containerId = '', isDarkMode = false) {
  if (!md) return null;
  // Very light conversion: split by lines and infer bullets/paragraphs
  const lines = String(md).split('\n');
  const elements = [];
  let list = null;
  let mermaidBlock = null;
  let mermaidIndex = 0;

  const flushList = () => {
    if (list) {
      elements.push(
        <ul 
          key={`ul-${elements.length}`} 
          style={{ 
            marginTop: spacing.xs, 
            marginBottom: spacing.sm, 
            paddingLeft: spacing.lg,
            ...typography.body,
            color: isDarkMode ? '#cbd5e1' : colors.text
          }}
        >
          {list}
        </ul>
      );
      list = null;
    }
  };

  const flushMermaid = () => {
    if (mermaidBlock) {
      const mermaidId = `mermaid-${containerId}-${mermaidIndex++}`;
      const mermaidCode = mermaidBlock.join('\n');
      elements.push(
        <MermaidDiagram
          key={mermaidId}
          id={mermaidId}
          code={mermaidCode}
        />
      );
      mermaidBlock = null;
    }
  };

  lines.forEach((line, idx) => {
    const l = line.trim();
    
    // Check for mermaid code block
    if (l.startsWith('```mermaid')) {
      flushList();
      flushMermaid();
      mermaidBlock = [];
      return;
    }
    if (l === '```' && mermaidBlock !== null) {
      flushList();
      flushMermaid();
      return;
    }
    if (mermaidBlock !== null) {
      mermaidBlock.push(l);
      return;
    }
    
    if (!l) {
      flushList();
      return;
    }
    if (l.startsWith('- ') || l.startsWith('* ')) {
      if (!list) list = [];
      list.push(<li key={`li-${idx}`} style={{ ...typography.body, color: isDarkMode ? '#cbd5e1' : colors.text }}>{l.slice(2)}</li>);
      return;
    }
    if (/^#{1,6}\s+/.test(l)) {
      flushList();
      const level = (l.match(/^#+/)[0] || '').length;
      const text = l.replace(/^#{1,6}\s+/, '');
      const Tag = `h${Math.min(level + 1, 6)}`;
      const headingStyle = level === 1 ? typography.h2 : typography.h3;
      elements.push(
        <Tag key={`h-${idx}`} style={{ ...headingStyle, marginTop: spacing.sm, marginBottom: spacing.xs, color: isDarkMode ? '#cbd5e1' : colors.text }}>
          {text}
        </Tag>
      );
      return;
    }
    // Bold text (markdown **text**)
    if (l.includes('**')) {
      flushList();
      const parts = l.split(/(\*\*[^*]+\*\*)/g);
      const formatted = parts.map((part, pIdx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={pIdx}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      elements.push(
        <p key={`p-${idx}`} style={{ ...typography.body, marginTop: spacing.xs, marginBottom: spacing.sm, color: isDarkMode ? '#cbd5e1' : colors.text }}>
          {formatted}
        </p>
      );
      return;
    }
    // paragraph
    flushList();
    elements.push(
      <p key={`p-${idx}`} style={{ ...typography.body, marginTop: spacing.xs, marginBottom: spacing.sm, color: isDarkMode ? '#cbd5e1' : colors.text }}>
        {l}
      </p>
    );
  });

  flushList();
  flushMermaid();
  return elements;
}

/**
 * DocsSection
 * Renders a section with anchor id and optional children.
 * Uses standardized styling for consistency.
 * Props:
 * - section: { id, title, content, children? }
 * - level: starting heading level (2 for h2)
 */
const DocsSection = ({ section, level = 2 }) => {
  const { language } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const [open, setOpen] = useState(true);
  const sectionRef = useRef(null);
  const HeadingTag = `h${Math.min(level, 6)}`;
  
  // Get translations - memoized to update when language changes
  const translations = useMemo(() => {
    const t = sharedTranslations[language] || sharedTranslations.en;
    return {
      collapseTitle: t?.legal?.section?.collapseTitle || 'Collapse',
      expandTitle: t?.legal?.section?.expandTitle || 'Expand',
      hide: t?.legal?.section?.hide || 'Hide',
      show: t?.legal?.section?.show || 'Show',
      copyLinkTitle: t?.legal?.section?.copyLinkTitle || 'Copy link'
    };
  }, [language]);

  // Mermaid rendering is now handled by MermaidDiagram component
  // No need for manual rendering logic here

  const sectionStyle = {
    marginBottom: sections.marginBottom,
    padding: sections.padding,
    borderRadius: sections.borderRadius,
    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : colors.borderLight}`,
    backgroundColor: isDarkMode ? '#1e293b' : colors.background
  };

  const headingContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: open ? spacing.sm : 0
  };

  const headingStyle = {
    ...(level === 2 ? typography.h2 : typography.h3),
    margin: 0,
    flex: 1,
    color: isDarkMode ? '#cbd5e1' : colors.text
  };

  const buttonStyle = {
    ...interactive.button,
    fontSize: typography.small.fontSize,
    padding: '4px 8px',
    color: isDarkMode ? '#cbd5e1' : colors.text,
    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : colors.border,
    backgroundColor: isDarkMode ? '#1e293b' : colors.background
  };

  const linkStyle = {
    ...interactive.link,
    ...typography.small,
    marginLeft: 'auto',
    textDecoration: 'none',
    color: isDarkMode ? colors.link : colors.link
  };

  const contentStyle = {
    marginTop: spacing.sm,
    padding: spacing.md,
    ...sections.content,
    color: isDarkMode ? '#cbd5e1' : colors.text
  };

  return (
    <section id={section.id} data-docs-id={section.id} style={sectionStyle}>
      <div style={headingContainerStyle}>
        <HeadingTag style={headingStyle}>{section.title}</HeadingTag>
        <button
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={`${section.id}-content`}
          title={open ? translations.collapseTitle : translations.expandTitle}
          style={buttonStyle}
          onMouseEnter={(e) => {
            e.target.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : colors.backgroundHover;
          }}
          onMouseLeave={(e) => {
            e.target.style.background = isDarkMode ? '#1e293b' : colors.background;
          }}
        >
          {open ? translations.hide : translations.show}
        </button>
        <a
          href={`#${section.id}`}
          title={translations.copyLinkTitle}
          style={linkStyle}
        >
          #{section.id}
        </a>
      </div>
      {open ? (
        <div id={`${section.id}-content`} ref={sectionRef} style={contentStyle}>
          {Array.isArray(section.content) ? section.content.map((item, i) => {
            // Handle React elements (like custom diagram components)
            if (React.isValidElement(item)) {
              return <React.Fragment key={i}>{item}</React.Fragment>;
            }
            // Handle HTML string items - render as HTML
            if (typeof item === 'string') {
              // Check if it's HTML (contains HTML tags)
              const isHTML = /<[a-z][\s\S]*>/i.test(item.trim());
              if (isHTML) {
                return (
                  <div
                    key={i}
                    dangerouslySetInnerHTML={{ __html: item }}
                    style={{ marginTop: spacing.xs, marginBottom: spacing.sm, color: isDarkMode ? '#cbd5e1' : colors.text }}
                  />
                );
              }
              // Otherwise render as plain text (for empty strings or plain text)
              if (item.trim() === '') {
                return null;
              }
              return (
                <p key={i} style={{ ...typography.body, marginTop: spacing.xs, marginBottom: spacing.sm, color: isDarkMode ? '#cbd5e1' : colors.text }}>
                  {item}
                </p>
              );
            }
            // Fallback for other types
            return (
              <p key={i} style={{ ...typography.body, marginTop: spacing.xs, marginBottom: spacing.sm, color: isDarkMode ? '#cbd5e1' : colors.text }}>
                {String(item)}
              </p>
            );
          }) : typeof section.content === 'string' && /<[a-z][\s\S]*>/i.test(section.content.trim()) ? (
            <div dangerouslySetInnerHTML={{ __html: section.content }} style={{ color: isDarkMode ? '#cbd5e1' : colors.text }} />
          ) : typeof section.content === 'string' ? (
            renderMarkdown(section.content, section.id, isDarkMode)
          ) : (
            <p style={{ ...typography.body, marginTop: spacing.xs, marginBottom: spacing.sm, color: isDarkMode ? '#cbd5e1' : colors.text }}>
              {String(section.content || '')}
            </p>
          )}
          {section.children && section.children.length > 0
            ? section.children.map(child => (
                <DocsSection key={child.id} section={child} level={Math.min(level + 1, 6)} />
              ))
            : null}
        </div>
      ) : null}
    </section>
  );
};

export default DocsSection;

